import React, { useState } from 'react';
import { CheckCircle2, XCircle, RotateCcw } from 'lucide-react';
import AuthorInfo from '../../components/common/AuthorInfo';

type Author = { name: string; fullName: string; avatar?: string };

export interface QuizQuestion {
 question: string;
 code?: string; // optional code snippet shown under the question
 options: string[];
 answer: number; // index of correct option
 explanation?: string;
}

/**
 * SectionQuiz — a lightweight, self-contained multiple-choice quiz. Pick an
 * option per question, submit, and see your score with per-question feedback.
 * Calm amber accent, consistent with the language articles.
 */
export const SectionQuiz: React.FC<{ title: string; intro?: string; questions: QuizQuestion[]; author?: Author }> = ({ title, intro, questions, author }) => {
 const [selected, setSelected] = useState<(number | null)[]>(() => questions.map(() => null));
 const [submitted, setSubmitted] = useState(false);

 const score = selected.reduce<number>((acc, sel, i) => (sel === questions[i].answer ? acc + 1 : acc), 0);
 const allAnswered = selected.every((s) => s !== null);

 const choose = (qi: number, oi: number) => {
  if (submitted) return;
  setSelected((prev) => prev.map((s, i) => (i === qi ? oi : s)));
 };

 const reset = () => {
  setSelected(questions.map(() => null));
  setSubmitted(false);
 };

 return (
  <div className="max-w-none">
   <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
    <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">{title}</h1>
    {intro && <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">{intro}</p>}
    {author && <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime={`${questions.length} questions`} />}
   </div>

   {/* Score banner after submit */}
   {submitted && (
    <div className={`mb-8 rounded-xl border-2 px-5 py-4 text-center ${score === questions.length ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20' : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20'}`}>
     <div className="text-2xl font-bold text-gray-900 dark:text-white">You scored {score} / {questions.length}</div>
     <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{score === questions.length ? 'Perfect — you know your ES6!' : 'Review the explanations below and try again.'}</div>
    </div>
   )}

   <div className="space-y-5">
    {questions.map((q, qi) => {
     const sel = selected[qi];
     return (
      <div key={qi} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
       <div className="flex items-start gap-2 mb-3">
        <span className="flex items-center justify-center w-6 h-6 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-bold flex-shrink-0">{qi + 1}</span>
        <p className="text-[15px] font-semibold text-gray-800 dark:text-gray-200">{q.question}</p>
       </div>
       {q.code && (
        <pre className="ml-8 mb-3 overflow-x-auto rounded-lg border border-gray-800 bg-gray-900 px-4 py-3 text-[13px] leading-6 text-gray-100 font-mono">
         <code>{q.code}</code>
        </pre>
       )}
       <div className="space-y-2 ml-8">
        {q.options.map((opt, oi) => {
         const isSelected = sel === oi;
         const isCorrect = oi === q.answer;
         let cls = 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50';
         if (submitted) {
          if (isCorrect) cls = 'border-green-400 dark:border-green-600 bg-green-50 dark:bg-green-900/20';
          else if (isSelected) cls = 'border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-900/20';
          else cls = 'border-gray-200 dark:border-gray-700 opacity-60';
         } else if (isSelected) {
          cls = 'border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20';
         }
         return (
          <button
           key={oi}
           onClick={() => choose(qi, oi)}
           disabled={submitted}
           className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg border text-[14px] text-gray-700 dark:text-gray-300 transition-colors ${cls}`}
          >
           {submitted && isCorrect && <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />}
           {submitted && isSelected && !isCorrect && <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
           <span>{opt}</span>
          </button>
         );
        })}
       </div>
       {submitted && q.explanation && (
        <div className="ml-8 mt-3 text-[13px] leading-6 text-gray-600 dark:text-gray-400 border-l-2 border-amber-400 pl-3">
         {q.explanation}
        </div>
       )}
      </div>
     );
    })}
   </div>

   <div className="flex items-center justify-center gap-3 mt-8">
    {!submitted ? (
     <button
      onClick={() => setSubmitted(true)}
      disabled={!allAnswered}
      className="px-5 py-2.5 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors disabled:opacity-40"
     >
      Check Answers
     </button>
    ) : (
     <button onClick={reset} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
      <RotateCcw className="w-4 h-4" /> Try Again
     </button>
    )}
   </div>
   {!allAnswered && !submitted && (
    <p className="mt-3 text-center text-xs text-gray-500 dark:text-gray-500">Answer all {questions.length} questions to check your score.</p>
   )}
  </div>
 );
};

export default SectionQuiz;
